"use client";

import { useCopyToClipboard } from "@/hooks/common";
import { highlightCodeSync } from "@/utils/shiki";
import { Check, Copy, FileText } from "lucide-react";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import styles from "./ShikiCodeBlock.module.scss";

// Shiki-based CodeBlock component with VS Code-like themes
const ShikiCodeBlock = ({
  code,
  language = "javascript",
  filename = null,
  showLineNumbers = false,
  tabs = null,
  className = "",
  ...props
}) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  // Synchronous highlighting - no loading states, no flashing
  const highlightedHTML = useMemo(() => {
    if (!code) return "";
    return highlightCodeSync(code, language, "dark");
  }, [code, language]);

  const handleCopy = () => {
    copyToClipboard(code);
  };

  // Handle tabs if provided
  if (tabs && Array.isArray(tabs) && tabs.length > 1) {
    return <TabbedCodeBlock tabs={tabs} className={className} {...props} />;
  }

  return (
    <div className={`${styles.codeBlock} ${className}`}>
      {/* Header only when filename exists */}
      {filename && (
        <div className={styles.header}>
          <div className={styles.filename}>
            <FileText size={14} />
            <span>{filename}</span>
          </div>
        </div>
      )}

      {/* Code content - always shows immediately */}
      <div className={styles.codeContent}>
        <div dangerouslySetInnerHTML={{ __html: highlightedHTML }} />
      </div>

      {/* Floating copy button */}
      <button
        onClick={handleCopy}
        className={styles.copyButton}
        aria-label="Copy code to clipboard"
      >
        {isCopied ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </div>
  );
};

// Tabbed code block component for multiple files
const TabbedCodeBlock = ({ tabs, className = "", ...props }) => {
  const [activeTab, setActiveTab] = React.useState(0);
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  const activeTabData = tabs[activeTab];
  const activeCode = activeTabData?.code || "";

  // Synchronous highlighting for active tab
  const highlightedHTML = useMemo(() => {
    if (!activeCode) return "";
    return highlightCodeSync(
      activeCode,
      activeTabData?.language || "javascript",
      "dark"
    );
  }, [activeCode, activeTabData?.language]);

  const handleCopy = () => {
    copyToClipboard(activeCode);
  };

  return (
    <div
      className={`${styles.codeBlock} ${styles.tabbedCodeBlock} ${className}`}
    >
      {/* Tab headers */}
      <div className={styles.tabHeader}>
        <div className={styles.tabs}>
          {tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`${styles.tab} ${index === activeTab ? styles.activeTab : ""}`}
            >
              <FileText size={14} />
              <span>{tab.filename || `File ${index + 1}`}</span>
            </button>
          ))}
        </div>
        <button
          onClick={handleCopy}
          className={styles.copyButton}
          aria-label="Copy code to clipboard"
        >
          {isCopied ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>

      {/* Active tab content - always shows immediately */}
      <div
        className={styles.codeContent}
        dangerouslySetInnerHTML={{ __html: highlightedHTML }}
      />
    </div>
  );
};

ShikiCodeBlock.propTypes = {
  code: PropTypes.string.isRequired,
  language: PropTypes.string,
  filename: PropTypes.string,
  showLineNumbers: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      language: PropTypes.string,
      highlightLines: PropTypes.arrayOf(PropTypes.number),
    })
  ),
  className: PropTypes.string,
};

ShikiCodeBlock.displayName = "ShikiCodeBlock";

export default ShikiCodeBlock;
